import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Duration from 'component/Search/Duration';
import { defaultConfig } from 'component/Search/Duration/config';
import { ActiveDurationLayer } from 'component/Search/Duration/types';
import { CustomSearchInputClassList } from 'component/Search/Input/types';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { useTravelDuration } from 'hooks/form/search';
import { QaIdentifier, useQa } from 'hooks/useQa';

import {
    crossValidationErrorAtom,
    fallbackNoteVisibility,
} from 'data/Atom/Component';
import {
    changedDaysAtom,
    changedOpenTypeAtom,
    changedTooltipAtom,
} from 'data/Atom/IntegratedOvernight';

import { ReactComponent as IconCross } from 'media/svg/cross.svg';

import { DurationValue, ValidationType } from 'types/Search/form';

import { ToggleFormElementContext } from '../../__context/toggleFormElementProvider';
import {
    CrossValidationError,
    useCrossDateValidation,
} from '../../__hooks/useCrossDateValidation';
import { ChangedHighlightType } from '../../types';

import DurationError from './DurationError';
import FallbackNoteContent from './FallbackNoteContent';

import styles from './DurationLayer.module.scss';

const MemoDuration = React.memo(Duration);

interface DurationLayerProps {
    initialValue?: DurationValue;
    label: string;
    placeholder: string;
    customClassList?: CustomSearchInputClassList;
    onValidate?: (type: ValidationType, isEmpty: boolean) => void;
}

const DurationLayer = ({
    initialValue,
    label,
    placeholder,
    customClassList,
    onValidate,
}: DurationLayerProps) => {
    const { t } = useTranslation();
    const qaAttr = useQa(QaIdentifier.Search_Form_FallbackNote);
    const [durationValue, setDurationValue] = useTravelDuration();
    const [error, setError] = useAtom(crossValidationErrorAtom);
    const { activeDurationLayer, setActiveDurationLayer } = useContext(
        ToggleFormElementContext
    );
    const isFallbackNoteVisible = useAtomValue(fallbackNoteVisibility);
    const setIsFallbackNoteVisible = useSetAtom(fallbackNoteVisibility);
    const openType = useAtomValue(changedOpenTypeAtom);
    const setShowTooltip = useSetAtom(changedTooltipAtom);
    const [showHighlightData, setShowHighlightData] = useAtom(changedDaysAtom);

    useEffect(() => {
        if (openType === ChangedHighlightType.Days) {
            setActiveDurationLayer(ActiveDurationLayer.DurationLayer);
        }
    }, [openType, setActiveDurationLayer]);

    const { validateDuration } = useCrossDateValidation();

    const crossValidateSelection = useCallback(
        (value: DurationValue) => {
            const validationResult = validateDuration(value);
            setError(validationResult);
        },
        [setError, validateDuration]
    );

    const onClose = useCallback(() => {
        if (error === CrossValidationError.MaxDurationExceeded) {
            setDurationValue(defaultConfig.fallback.durationExceeded);
            setIsFallbackNoteVisible(true);
        }
        setActiveDurationLayer(ActiveDurationLayer.None);
        setShowHighlightData(false);
    }, [
        error,
        setActiveDurationLayer,
        setDurationValue,
        setIsFallbackNoteVisible,
        setShowHighlightData,
    ]);

    const onValueChange = useCallback(
        (value: DurationValue) => {
            crossValidateSelection(value);
            setShowHighlightData(false);
            setShowTooltip(false);
            setDurationValue(value);
            onValidate?.(ValidationType.Duration, value.type === 'empty');
            onClose();
        },
        [
            crossValidateSelection,
            setShowHighlightData,
            setShowTooltip,
            setDurationValue,
            onValidate,
            onClose,
        ]
    );

    const highlightComponent = useMemo(
        () =>
            showHighlightData ? (
                <div
                    dangerouslySetInnerHTML={{
                        __html: t('TRANS-duration-changed-review-the-choice'),
                    }}
                />
            ) : undefined,
        [showHighlightData, t]
    );

    useEffect(() => {
        if (initialValue) {
            setDurationValue(initialValue);
        }
    }, [initialValue, setDurationValue]);

    return (
        <div className={styles.DurationContainer}>
            <MemoDuration
                config={defaultConfig}
                value={durationValue}
                onValueChange={onValueChange}
                errorComponent={<DurationError error={error} />}
                highlightComponent={highlightComponent}
                isLayerOpen={activeDurationLayer}
                onOpen={() =>
                    setActiveDurationLayer(ActiveDurationLayer.DurationLayer)
                }
                onClose={onClose}
                label={label}
                placeholder={placeholder}
                customClassList={customClassList}
                onValidate={onValidate}
            />
            {isFallbackNoteVisible && (
                <div className={styles.FallbackDurationExceeded} {...qaAttr}>
                    <span className={styles.Arrow} />

                    <button
                        className={styles.CrossButton}
                        onClick={() => setIsFallbackNoteVisible(false)}
                    >
                        <IconCross />
                    </button>

                    <FallbackNoteContent
                        error={CrossValidationError.MaxDurationExceeded}
                    />
                </div>
            )}
        </div>
    );
};

export default DurationLayer;
